import { AdminDataTable, AdminTableCheckbox } from './AdminDataTable';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Hint, Spinner } from '@src/components';
import { CopyTag } from './CopyTag';

type Invitation = {
  id: string; giftId: number; userId: number; boxId: number; username: string | null;
  status: string; explicit: boolean; createdAt: string; shownAt: string | null;
  expiresAt: string | null; resolvedAt: string | null; openedAt: string | null; thankedAt: string | null; returnedAt: string | null; previousStatus: string | null;
};
export function GiftInvitationHistory({ onDenied, onReturned }: { onDenied: () => void; onReturned: () => void }) {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<Invitation[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [returning, setReturning] = useState(false);
  const selectable = rows.filter(row => ['queued', 'locked', 'expired', 'dismissed'].includes(row.status));
  const allSelected = selectable.length > 0 && selectable.every(row => selected.has(row.id));
  async function returnToPool() {
    if (returning || !selected.size) return;
    setReturning(true);
    setError(false);
    try {
      const res = await fetch('/api/admin/santa-gifts/invitations/return-to-pool', {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invitationIds: [...selected] }),
      });
      if ([401, 403].includes(res.status)) onDenied();
      if (!res.ok) throw Error();
      const result = await res.json();
      setSelected(new Set());
      await load(null);
      if (result.skippedIds.length) setError(true);
      else onReturned();
    } catch { setError(true); }
    finally { setReturning(false); }
  }
  async function load(before: string | null, signal?: AbortSignal) {
    setLoading(true);
    setError(false);
    try {
      const res = await fetch(`/api/admin/santa-gifts/invitations${before ? `?before=${encodeURIComponent(before)}` : ''}`, { credentials: 'same-origin', cache: 'no-store', signal });
      if ([401, 403].includes(res.status)) onDenied();
      if (!res.ok) throw Error();
      const data: { invitations: Invitation[]; nextCursor: string | null } = await res.json();
      if (signal?.aborted) return;
      setRows(old => before ? [...old, ...data.invitations] : data.invitations);
      setCursor(data.nextCursor);
    } catch {
      if (!signal?.aborted) setError(true);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }
  useEffect(() => {
    const controller = new AbortController();
    void load(null, controller.signal);
    return () => controller.abort();
    // Authorization callback changes must not reset pagination.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (loading || returning) return;
    const controller = new AbortController();
    let pending = false;
    const timer = window.setInterval(() => {
      if (pending) return;
      pending = true;
      void (async () => {
        // Re-fetch the loaded range without resetting pagination or selection.
        const updated: Invitation[] = [];
        let before: string | null = null;
        do {
          const res = await fetch(`/api/admin/santa-gifts/invitations${before ? `?before=${encodeURIComponent(before)}` : ''}`, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
          if ([401, 403].includes(res.status)) onDenied();
          if (!res.ok) throw Error();
          const data: { invitations: Invitation[]; nextCursor: string | null } = await res.json();
          updated.push(...data.invitations);
          before = data.nextCursor;
        } while (before && updated.length < rows.length);
        if (controller.signal.aborted) return;
        setRows(updated);
        setCursor(before);
        const eligible = new Set(updated.filter(row => ['queued', 'locked', 'expired', 'dismissed'].includes(row.status)).map(row => row.id));
        setSelected(old => new Set([...old].filter(id => eligible.has(id))));
      })().catch(() => { if (!controller.signal.aborted) setError(true); }).finally(() => { pending = false; });
    }, 60_000);
    return () => { controller.abort(); window.clearInterval(timer); };
    // Callback identity must not postpone scheduled refreshes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, returning, rows.length]);
  const time = (value: string | null) => value ? new Date(value).toLocaleString(i18n.language) : '—';
  return <section data-testid="gift-invitation-history">
    <h3>{t('admin.invitationHistory.title')}</h3>
    {error && <Hint tone="error">{t('admin.userGifts.error')}</Hint>}
    {!loading && !error && !rows.length && <p>{t('admin.invitationHistory.empty')}</p>}
    {!!rows.length && <p>{t('admin.invitationHistory.poolHint')}</p>}
    {!!rows.length && <Button size="small" disabled={returning || loading || !selected.size} loading={returning} onClick={() => void returnToPool()}>{t('admin.invitationHistory.returnToPool', { count: selected.size })}</Button>}
    {!!rows.length && <div className="admin-payments__scroll"><AdminDataTable paginate>
      <thead><tr><th><AdminTableCheckbox label={t('admin.invitationHistory.selectAll')} checked={allSelected} mixed={selected.size > 0 && !allSelected} disabled={returning || !selectable.length} reason={i18n.language.startsWith('ru') ? 'Нет доступных приглашений' : 'No eligible invitations'} onChange={() => setSelected(allSelected ? new Set() : new Set(selectable.slice(0, 100).map(row => row.id)))} /></th>{['invitation', 'recipient', 'gift', 'box', 'source', 'status', 'createdAt', 'shownAt', 'expiresAt', 'resolvedAt', 'openedAt', 'thankedAt', 'returnedAt'].map(key => <th key={key}>{t(`admin.invitationHistory.${key}`)}</th>)}</tr></thead>
      <tbody>{rows.map(row => <tr key={row.id}>
        <td><AdminTableCheckbox label={t('admin.invitationHistory.selectInvitation', { id: row.id })} checked={selected.has(row.id)} disabled={returning || !['queued','locked','expired','dismissed'].includes(row.status) || (!selected.has(row.id) && selected.size >= 100)} reason={row.status === 'accepted' ? (i18n.language.startsWith('ru') ? 'Подарок принят' : 'Gift accepted') : row.status === 'returned' ? (i18n.language.startsWith('ru') ? 'Уже в пуле' : 'Already returned') : returning ? (i18n.language.startsWith('ru') ? 'Обработка' : 'Processing') : (i18n.language.startsWith('ru') ? 'Лимит 100' : 'Limit of 100')} onChange={() => setSelected(old => { const next = new Set(old); if (next.has(row.id)) next.delete(row.id); else next.add(row.id); return next; })} /></td>
        <td><CopyTag value={row.id} prefix="invitation" label={t('admin.invitationHistory.invitation')} /></td>
        <td>{row.username && <div>{row.username}</div>}<CopyTag value={String(row.userId)} prefix="user" label={t('admin.userGifts.copy.user')} /></td>
        <td><CopyTag value={String(row.giftId)} prefix="gift" label={t('admin.userGifts.copy.gift')} /></td>
        <td><CopyTag value={String(row.boxId)} prefix="box" label={t('admin.userGifts.copy.box')} /></td>
        <td>{t(`admin.invitationHistory.${row.explicit ? 'explicit' : 'automatic'}`)}</td>
        <td>{t(`admin.invitationHistory.statuses.${row.status}`)}</td>
        {(['createdAt', 'shownAt', 'expiresAt', 'resolvedAt', 'openedAt', 'thankedAt', 'returnedAt'] as const).map(key => <td key={key}>{time(row[key])}

        </td>)}
      </tr>)}</tbody>
    </AdminDataTable></div>}
    {loading && <Spinner label={t('account.loading')} />}
    {!loading && (cursor || error) && <Button variant="secondary" onClick={() => void load(cursor)}>{t(error ? 'admin.invitationHistory.retry' : 'admin.invitationHistory.more')}</Button>}
  </section>;
}
